import { useState, useEffect } from 'react';
import { Cloud, Download, Upload, Settings, Shield, Clock, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { Modal } from '../ui/Modal';
import { dataBackupService, BackupProvider, BackupHistoryEntry } from '@/services/dataBackupService';
import { simplifiedDriveService } from '@/services/googleDriveSimplified';
import { cloudSyncService } from '@/services/cloudSyncService';

export function BackupManager() {
  const [providers, setProviders] = useState<BackupProvider[]>([]);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [backupHistory, setBackupHistory] = useState<BackupHistoryEntry[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [lastBackupTime, setLastBackupTime] = useState<Date | null>(null);
  const [dataSize, setDataSize] = useState<string>('');
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);
  const [isDriveConnected, setIsDriveConnected] = useState(simplifiedDriveService.isConfigured());

  useEffect(() => {
    loadProviders();
    loadBackupHistory();
    loadBackupStatus();

    // Handle Google Drive OAuth callback
    const params = new URLSearchParams(window.location.search);
    const driveTokens = params.get('drive_tokens');
    if (driveTokens) {
      simplifiedDriveService.saveTokensFromUrl(driveTokens).then(() => {
        setIsDriveConnected(true);
        cloudSyncService.refreshConnectionStatus();
        loadProviders();
        showMessage('success', 'Google Drive connected successfully');
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      }).catch(() => {
        showMessage('error', 'Failed to connect Google Drive');
      });
    }
  }, []);

  const loadProviders = () => {
    const availableProviders = dataBackupService.getAvailableProviders();
    setProviders(availableProviders);
  };

  const loadBackupHistory = () => {
    const history = dataBackupService.getBackupHistory();
    setBackupHistory(history);
  };

  const loadBackupStatus = () => {
    setLastBackupTime(dataBackupService.getLastBackupTime());
    setDataSize(dataBackupService.getDataSizeFormatted());
    setAutoBackupEnabled(dataBackupService.isAutoBackupEnabled());
  };

  const showMessage = (type: 'success' | 'error' | 'warning', message: string) => {
    setStatusMessage({ type, message });
    setTimeout(() => setStatusMessage(null), 5000);
  };

  const handleGoogleDriveConnect = () => {
    simplifiedDriveService.connectDrive();
  };

  const handleDisconnectDrive = () => {
    cloudSyncService.disconnect();
    setIsDriveConnected(false);
    loadProviders();
    showMessage('success', 'Google Drive disconnected');
  };

  const handleBackup = async (providerId: string) => {
    setIsBackingUp(true);
    try {
      switch (providerId) {
        case 'local':
          await dataBackupService.backupToLocalDevice();
          showMessage('success', 'Backup saved to local device successfully');
          break;
        case 'google_drive':
          await dataBackupService.backupToGoogleDrive();
          showMessage('success', 'Backup saved to Google Drive successfully');
          break;
        default:
          throw new Error(`Provider ${providerId} not implemented`);
      }

      loadBackupHistory();
      loadBackupStatus();
    } catch (error: any) {
      console.error('Backup failed:', error);
      showMessage('error', `Backup failed: ${error.message}`);
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestore = async (providerId: string) => {
    const confirmed = window.confirm(
      'Restoring a backup will replace all your current data. Are you sure you want to continue?'
    );

    if (!confirmed) return;

    setIsRestoring(true);
    try {
      switch (providerId) {
        case 'local':
          await dataBackupService.loadFromLocalDevice();
          showMessage('success', 'Data restored successfully. Refreshing application...');
          // Give user time to see the message before refresh
          setTimeout(() => window.location.reload(), 2000);
          break;
        case 'google_drive':
          await dataBackupService.loadFromGoogleDrive();
          showMessage('success', 'Data restored from Google Drive successfully. Refreshing application...');
          setTimeout(() => window.location.reload(), 2000);
          break;
        default:
          throw new Error(`Restore from ${providerId} not implemented`);
      }
    } catch (error: any) {
      console.error('Restore failed:', error);
      showMessage('error', `Restore failed: ${error.message}`);
      setIsRestoring(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString()
    };
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusIcon = (success: boolean) => {
    return success ? (
      <CheckCircle className="h-4 w-4 text-green-600" />
    ) : (
      <XCircle className="h-4 w-4 text-red-600" />
    );
  };

  const getTimeSinceLastBackup = () => {
    if (!lastBackupTime) return null;

    const now = new Date();
    const diffMs = now.getTime() - lastBackupTime.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return 'Less than an hour ago';
  };

  return (
    <div className="space-y-6">
      {/* Status Messages */}
      {statusMessage && (
        <Alert variant={statusMessage.type === 'error' ? 'destructive' : statusMessage.type === 'warning' ? 'warning' : 'default'}>
          {statusMessage.message}
        </Alert>
      )}

      {/* Backup Status Overview */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Backup Status
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(true)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Last Backup */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Last Backup</span>
            </div>
            {lastBackupTime ? (
              <div>
                <div className="text-sm text-green-600">{getTimeSinceLastBackup()}</div>
                <div className="text-xs text-muted-foreground">
                  {lastBackupTime.toLocaleDateString()} at {lastBackupTime.toLocaleTimeString()}
                </div>
              </div>
            ) : (
              <div className="text-sm text-orange-600">No backups yet</div>
            )}
          </div>

          {/* Data Size */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Cloud className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Data Size</span>
            </div>
            <div className="text-sm">{dataSize}</div>
            <div className="text-xs text-muted-foreground">Total backup size</div>
          </div>

          {/* Auto Backup Status */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Auto Backup</span>
            </div>
            <div className={`text-sm ${autoBackupEnabled ? 'text-green-600' : 'text-orange-600'}`}>
              {autoBackupEnabled ? 'Enabled' : 'Disabled'}
            </div>
            <div className="text-xs text-muted-foreground">
              {autoBackupEnabled ? 'Regular backups active' : 'Manual backups only'}
            </div>
          </div>
        </div>

        {!lastBackupTime && (
          <Alert variant="warning" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            No backups found. Create your first backup to protect your data.
          </Alert>
        )}
      </Card>

      {/* Backup Providers */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Backup Options</h3>
        <div className="grid gap-4 md:grid-cols-2">
          {providers.map(provider => (
            <div key={provider.id} className="border rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{provider.icon}</span>
                  <div>
                    <h4 className="font-medium">{provider.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {provider.requiresAuth ? 'Requires authentication' : 'No setup required'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {provider.isConfigured && (
                    <div className="text-green-600 text-sm flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Ready
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => handleBackup(provider.id)}
                  disabled={isBackingUp || !provider.isConfigured}
                  className="flex-1"
                >
                  {isBackingUp ? (
                    <>
                      <Clock className="h-3 w-3 mr-1 animate-spin" />
                      Backing up...
                    </>
                  ) : (
                    <>
                      <Upload className="h-3 w-3 mr-1" />
                      Backup
                    </>
                  )}
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRestore(provider.id)}
                  disabled={isRestoring || !provider.isConfigured}
                  className="flex-1"
                >
                  {isRestoring ? (
                    <>
                      <Clock className="h-3 w-3 mr-1 animate-spin" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <Download className="h-3 w-3 mr-1" />
                      Restore
                    </>
                  )}
                </Button>
              </div>

              {/* Provider-specific info */}
              {provider.id === 'local' && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Saves backup file directly to your device
                </div>
              )}

              {provider.id === 'google_drive' && !isDriveConnected && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleDriveConnect}
                  >
                    Connect Google Drive
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Stores backups in your personal Google Drive
                  </p>
                </div>
              )}

              {provider.id === 'google_drive' && isDriveConnected && (
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Syncs to your Google Drive
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-red-600 h-auto py-1 px-2"
                    onClick={handleDisconnectDrive}
                  >
                    Disconnect
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Backup History */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Backups</h3>
        {backupHistory.length > 0 ? (
          <div className="space-y-3">
            {backupHistory.slice(0, 10).map((backup) => {
              const timestamp = formatTimestamp(backup.timestamp);
              const provider = providers.find(p => p.id === backup.providerId);

              return (
                <div key={backup.id} className="flex flex-wrap items-center justify-between gap-2 py-3 border-b last:border-b-0">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(backup.success)}
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{provider?.icon || '❓'}</span>
                      <div>
                        <p className="text-sm font-medium">
                          {provider?.name || 'Unknown Provider'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {timestamp.date} at {timestamp.time}
                        </p>
                        {backup.error && (
                          <p className="text-xs text-red-600 mt-1">
                            Error: {backup.error}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatFileSize(backup.dataSize)}
                  </div>
                </div>
              );
            })}

            {backupHistory.length > 10 && (
              <div className="text-center pt-2">
                <p className="text-sm text-muted-foreground">
                  Showing 10 most recent backups
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Cloud className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No backup history available</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create your first backup to see history here
            </p>
          </div>
        )}
      </Card>

      {/* Settings Modal */}
      <Modal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        title="Backup Settings"
        size="lg"
      >
        <div className="space-y-6">
          {/* Privacy Information */}
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Data Privacy
            </h4>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                🔒 Your data stays with you. All backups are stored directly in your personal cloud storage accounts or local device. We never have access to your data.
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">Automatic Backup</h4>
            {isDriveConnected ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">
                  Google Drive is connected. Your data is automatically synced when changes are detected.
                </p>
              </div>
            ) : (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground">
                  Connect Google Drive to enable automatic cloud backups across all your devices.
                </p>
              </div>
            )}
          </div>

          {/* Backup Information */}
          <div>
            <h4 className="font-medium mb-2">Backup Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Current data size:</span>
                <div className="font-medium">{dataSize}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Total backups:</span>
                <div className="font-medium">{backupHistory.length}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Last successful backup:</span>
                <div className="font-medium">
                  {lastBackupTime ? getTimeSinceLastBackup() : 'Never'}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Success rate:</span>
                <div className="font-medium">
                  {backupHistory.length > 0
                    ? `${Math.round((backupHistory.filter(b => b.success).length / backupHistory.length) * 100)}%`
                    : 'N/A'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowSettings(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}